/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import React, { Suspense, lazy, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SideDrawerNew, { DRAWER_WIDTH } from "./SideDrawerNew";
import { authenticationService } from "~/services";
import { text } from "~/components/TextResources";
import { TopBarNew } from "./TopBar";
import { useAppSelector } from "~/store/hooks";
import { selectCurrentServiceController } from "~/store/context/dashboardSlice";
import { Grid } from '@h9/ui-lib';
import { settingsAirgapEnabled, settingsAssistantEnabled } from "~/store/context/settingsSlice";
import { useMobile } from "~/hooks/useMobile";
import { useGoingOutOfSupportReleaseToast, notifyGoingOutOfSupportEdgeFilterTransition } from "~/views/edges/GoingOutOfSupportReleaseCallout";
import { edgeEdgesWithPool } from "~/store/context/edgeSlice";
import { EDGES } from "~/constants/routes";
import { updateURL } from "~/helpers/history";

const CHATBOT_WIDTH = "25vw";
const CHATBOT_WIDTH_MOBILE = "100vw";
const Assistant = lazy(() => import("./assistant/Assistant"));

type Props = {
  children: React.ReactNode;
};

const styles = {
  wrapper: {
    background: '#226A58',
    height: "100vh",
    flexWrap: "nowrap",
    width: "100%",
    position: "relative"
  },
  main: {
    flexGrow: 1,
    maxWidth: `100%`,
    overflow: "auto",
    height: "100%",
    background: '#F7F7F6',
    padding: {
      xs: '8px',
      sm: '12px',
      md: '16px'
    }
  },
  container: {
    overflow: "auto",
    flexGrow: 1,
    flexDirection: "column",
    flexWrap: "nowrap",
    maxWidth: {
      xs: '100%',
      sm: `calc(100% - ${DRAWER_WIDTH}px)`
    },
    paddingTop: {
      xs: '8px',
      sm: '12px',
      md: '16px'
    },

  },
};

const LayoutNew = (props: Props) => {
  const serviceInstanceDisplayName = useAppSelector(selectCurrentServiceController)?.displayName!;
  const serviceInstanceName = authenticationService?.currentUserValue?.serviceInstanceName ?? "";

  useEffect(() => {
    const title = `${serviceInstanceDisplayName ? serviceInstanceDisplayName : serviceInstanceName} - ${text.title}`
    document.title = import.meta.env.DEV ? `DEV - ${title}` : title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceInstanceDisplayName]);

  const botDocked = useAppSelector((state) => state.utility.chatBot.maximize && state.utility.chatBot.open);
  const isAssistantEnabled = useAppSelector(settingsAssistantEnabled);
  const AirGapEnabled = useAppSelector(settingsAirgapEnabled);
  const uniqueIdForBody = "bodyToScrollInLayout";
  const isMobile = useMobile();
  const edgesWithPools = useAppSelector(edgeEdgesWithPool);
  const history = useHistory();
  const location = useLocation();

  const handleViewGoingOutOfSupportEdges = () => {
    if (location.pathname === EDGES) {
      updateURL({ goingOutOfSupportOnly: "true" });
      notifyGoingOutOfSupportEdgeFilterTransition();
      return;
    }
    notifyGoingOutOfSupportEdgeFilterTransition();
    history.push(`${EDGES}?goingOutOfSupportOnly=true`);
  };

  useGoingOutOfSupportReleaseToast(edgesWithPools, handleViewGoingOutOfSupportEdges);

  return (
    <Grid container sx={styles.wrapper}>
      <SideDrawerNew />
      <Grid container sx={styles.container}>
        <TopBarNew title={serviceInstanceDisplayName} subtitle={text.topBarSubtitle} bodyId={uniqueIdForBody} />
        <Grid component="main" id={uniqueIdForBody} sx={{
          ...styles.main,
          maxWidth: botDocked ? (isMobile ? `calc(100% - ${CHATBOT_WIDTH_MOBILE})` : `calc(100% - ${CHATBOT_WIDTH})`) : `100%`
        }}>
          {props.children}
        </Grid>
      </Grid>
      {(!AirGapEnabled && isAssistantEnabled) && (
        <Suspense fallback={null}>
          <Assistant />
        </Suspense>
      )}
    </Grid>
  );
};

export default LayoutNew;
